import { Spacing } from '@persona-id/design-tokens';
import { ChevronLeftIcon } from '@persona-id/design-tokens/icons';
import { ReactNode, useCallback, useState } from 'react';
import { ValidateResult } from 'react-hook-form';
import styled from 'styled-components';

import { DEFAULT_MODULE_VISIBILITY_CONDITIONAL } from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor';
import ComputedPropertyInput from 'dashboard/apps/DashboardLayoutEditor/ComputedPropertyInput';
import {
  AlternativeModuleNames,
  MODULE_TYPE_TO_NAME,
  ModuleTypes,
} from 'dashboard/apps/DashboardLayoutEditor/constants';
import {
  ALTERNATIVE_MODULE_TITLE,
  FULL_HEIGHT_MODULES,
  ModuleTypeInfo,
} from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/constants';
import ControlPanelContainer from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelContainer';
import ControlPanelDivider from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelDivider';
import { ComponentLibrary, Tabs } from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels';
import { DeleteModule, UpdateModule } from 'dashboard/apps/DashboardLayoutEditor/LayoutEditor';
import Accordion from 'dashboard/components/Accordion';
import TabBarSwitch from 'dashboard/components/TabBarSwitch';
import TabSwitch from 'dashboard/components/TabBarSwitch/TabSwitch';
import { ComputedProperty } from 'dashboard/lib/case-layouts/computed-properties';
import { BaseModule } from 'dashboard/lib/module';
import { Environment, EnvironmentName } from 'dashboard/models/environment';
import { Configuration, Tab } from 'dashboard/models/grid-layout';
import { Organization } from 'dashboard/models/organization';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { headingMixins } from 'design-system/mixins/text';
import { JsonSchema } from 'lib/json-schema';

const Container = styled.div`
  background-color: ${({ theme }) => theme.color.background.primary};
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

const ControlPanelHeader = styled.div`
  padding: ${Spacing[5]};
  flex: none;

  ${headingMixins.sm};
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  gap: ${Spacing[2]};
`;

const ControlPanelContent = styled.div`
  overflow-y: visible;
  flex: auto;
`;

const ToggleRowWrapper = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const ToggleLabelContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  justify-content: space-between;
`;

const LabelDescription = styled(Paragraph)`
  color: ${(props) => props.theme.color.text.tertiary};
`;

export interface ControlPanelProps<
  M extends BaseModule,
  T extends Tab<M>,
  SupportedModuleTypes extends ModuleTypes,
> {
  organization: Organization;
  currentEnvironment: Environment;
  modulesInfo: { module: M; tabToken: string }[];
  layoutConfiguration: Configuration<M>;
  selectedModuleToken: string;
  environmentName: EnvironmentName;
  dataSchema: JsonSchema;
  shouldShowVisibilityToggle: boolean;
  shouldShowVisibilityAdvancedControls: boolean;
  shouldShowAdvancedOptions: boolean;
  supportedModuleTypes: Record<string, ModuleTypeInfo<SupportedModuleTypes>>;
  supportedComponentTypes: Record<string, ModuleTypeInfo<SupportedModuleTypes>>;
  generateDefaultModuleConfiguration: (moduleType: SupportedModuleTypes) => M;
  resetToComponentLibrary: () => void;
  updateModule: UpdateModule<M>;
  deleteModule: DeleteModule;
  addTab: (name: string) => void;
  updateTabs: (tabs: Tab<M>[]) => void;
  setCaseTemplateIsValid: (valid: boolean) => void;
  renderModuleConfiguration: (moduleType: M, handleUpdateModule: (module: M) => void) => ReactNode;
  renderTabForm: (
    tab: T,
    handleHideModal: () => void,
    handleEdit: (tab: T) => void,
    handleValidateTabName: (tabName: string, tabToken?: string) => ValidateResult,
  ) => ReactNode;
}

function ControlPanel<
  M extends BaseModule,
  T extends Tab<M>,
  SupportedModuleTypes extends ModuleTypes,
>({
  organization,
  currentEnvironment,
  modulesInfo,
  layoutConfiguration,
  selectedModuleToken,
  environmentName,
  dataSchema,
  shouldShowVisibilityToggle,
  shouldShowVisibilityAdvancedControls,
  shouldShowAdvancedOptions,
  supportedModuleTypes,
  supportedComponentTypes,
  generateDefaultModuleConfiguration,
  resetToComponentLibrary,
  updateModule,
  deleteModule,
  addTab,
  updateTabs,
  setCaseTemplateIsValid,
  renderModuleConfiguration,
  renderTabForm,
}: ControlPanelProps<M, T, SupportedModuleTypes>) {
  const selectedModuleInfo = selectedModuleToken
    ? modulesInfo.find((moduleInfo) => moduleInfo.module.token === selectedModuleToken)
    : undefined;

  const visible = selectedModuleInfo?.module['module-layout'].visible;

  const enabled = selectedModuleInfo?.module['module-layout'].enabled ?? {
    'return-type': 'boolean',
    'is-required': true,
    'resolution-mechanism': {
      'resolution-type': 'custom-code',
      'serialized-code': null,
    },
  };

  const isConditionalLogic =
    visible?.['resolution-mechanism']['resolution-type'] === 'conditional-logic';

  // Keep track of which module tokens have invalid conditional logic rules
  const [invalidModuleTokens, setInvalidModuleTokens] = useState<string[]>([]);

  // Keep track of which modules have valid conditional rules by
  // adding them to an invalid module tokens list
  const updateInvalidModuleTokens = useCallback(
    (moduleToken: string, valid: boolean) => {
      if (valid && invalidModuleTokens.includes(moduleToken)) {
        const index = invalidModuleTokens.findIndex((m) => m === moduleToken);
        setInvalidModuleTokens(invalidModuleTokens.splice(index, 1));
      } else if (!valid && !invalidModuleTokens.includes(moduleToken)) {
        setInvalidModuleTokens([...invalidModuleTokens, moduleToken]);
      }

      // Notify state of the new case template validity to allow/prevent saving the case template
      setCaseTemplateIsValid(valid && invalidModuleTokens.length === 0);
    },
    [JSON.stringify(invalidModuleTokens), setCaseTemplateIsValid],
  );

  const handleUpdateModule = (module: M) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    updateModule(selectedModuleInfo.tabToken, module);
  };

  const toggleModuleStaticHeight = () => {
    handleUpdateModule({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ...selectedModuleInfo.module,
      'module-layout': {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...selectedModuleInfo.module['module-layout'],
        // toggle dynamicHeights based on the current state
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'dynamic-height': !selectedModuleInfo.module['module-layout']['dynamic-height'],
      },
    });
  };

  const updateVisibilityComputedProperty = (computedProperty: ComputedProperty) => {
    handleUpdateModule({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ...selectedModuleInfo.module,
      'module-layout': {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...selectedModuleInfo.module['module-layout'],
        visible: computedProperty,
      },
    });
  };

  const deleteDynamicVisibleSetting = () => {
    handleUpdateModule({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ...selectedModuleInfo.module,
      'module-layout': {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...selectedModuleInfo.module['module-layout'],
        visible: null,
      },
    });
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    updateInvalidModuleTokens(selectedModuleInfo.module.token, true);
  };

  const updateEnablementComputedProperty = (computedProperty: ComputedProperty) => {
    handleUpdateModule({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ...selectedModuleInfo.module,
      'module-layout': {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...selectedModuleInfo.module['module-layout'],
        enabled: computedProperty,
      },
    });
  };

  const setDefaultVisibleRules = () => {
    const dynamicModuleDefault: ComputedProperty = {
      'return-type': 'boolean',
      'is-required': true,
      'default-value': false,
      'resolution-mechanism': {
        'resolution-type': 'conditional-logic',
        conditional: DEFAULT_MODULE_VISIBILITY_CONDITIONAL,
      },
    };
    updateVisibilityComputedProperty(dynamicModuleDefault);
    // Since the default rule is empty, it is invalid
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    updateInvalidModuleTokens(selectedModuleInfo.module.token, false);
  };

  const setDefaultVisibleCode = () => {
    const customCodeDefault: ComputedProperty = {
      'return-type': 'boolean',
      'is-required': true,
      'resolution-mechanism': {
        'resolution-type': 'custom-code',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'serialized-code': null,
      },
    };
    updateVisibilityComputedProperty(customCodeDefault);
    // We do not validate custom code at the moment
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    updateInvalidModuleTokens(selectedModuleInfo.module.token, true);
  };

  const toggleDynamicVisibilitySetting = (visible: boolean) => {
    if (visible) {
      deleteDynamicVisibleSetting();
    } else {
      setDefaultVisibleRules();
    }
  };

  const controlPanelHeaderText = () => {
    if (selectedModuleInfo == null) {
      return ALTERNATIVE_MODULE_TITLE[selectedModuleToken as AlternativeModuleNames];
    }

    if (
      selectedModuleInfo.module.type === ModuleTypes.Resource &&
      'field-key' in selectedModuleInfo.module
    ) {
      switch (selectedModuleInfo.module['field-key']) {
        case 'accounts':
          return 'Account';
        case 'inquiries':
          return 'Inquiry';
        case 'reports':
          return 'Report';
        case 'report-runs':
          return 'Report Runs';
        case 'transactions':
          return 'Transaction';
        case 'verifications':
          return 'Verification';
      }
    }

    return MODULE_TYPE_TO_NAME[selectedModuleInfo.module.type];
  };

  const renderControlPanelContent = () => {
    if (selectedModuleInfo == null) {
      switch (selectedModuleToken) {
        case AlternativeModuleNames.ComponentLibrary: {
          return (
            <ComponentLibrary
              organization={organization}
              currentEnvironment={currentEnvironment}
              supportedModuleTypes={supportedModuleTypes}
              supportedComponentTypes={supportedComponentTypes}
              generateDefaultModuleConfiguration={generateDefaultModuleConfiguration}
            />
          );
        }
        case AlternativeModuleNames.Tabs: {
          return (
            <Tabs
              tabs={layoutConfiguration.tabs}
              environmentName={environmentName}
              addTab={addTab}
              updateTabs={updateTabs}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              renderTabForm={renderTabForm}
            />
          );
        }
        default:
          return null;
      }
    }

    return (
      <span key={selectedModuleToken}>
        {renderModuleConfiguration(selectedModuleInfo.module, handleUpdateModule)}
      </span>
    );
  };

  return (
    <Container id='control-panel' data-test='control-panel'>
      <ControlPanelHeader
        id={`control-panel__header--${environmentName}`}
        data-test={`control-panel__header--${environmentName}`}
      >
        <HeaderContainer>
          {selectedModuleToken !== AlternativeModuleNames.ComponentLibrary && (
            <Button
              id={`control-panel__header--back-button--${environmentName}`}
              data-test={`control-panel__header--back-button--${environmentName}`}
              width='icon'
              variant='inverse'
              prefixIcon={<ChevronLeftIcon />}
              prefixIconType='basic'
              type='button'
              onClick={resetToComponentLibrary}
            />
          )}
          {controlPanelHeaderText()}
        </HeaderContainer>
      </ControlPanelHeader>
      <ControlPanelContent
        id={`control-panel__content--${environmentName}`}
        data-test={`control-panel__content--${environmentName}`}
      >
        {renderControlPanelContent()}
        <ControlPanelDivider full />
      </ControlPanelContent>
      {selectedModuleInfo && (
        <>
          {selectedModuleInfo.module?.['module-layout']['dynamic-height'] != null && (
            <>
              <ControlPanelContainer>
                <Heading size='sm'>Module Layout</Heading>
                <Spacer height={3} />
                <ToggleRowWrapper>
                  <ToggleLabelContainer>
                    <Label size='md' weight='bold'>
                      Dynamic height
                    </Label>
                    <LabelDescription size='sm'>
                      Module will grow or shrink to fit its contents.
                    </LabelDescription>
                  </ToggleLabelContainer>
                  <ToggleSlider
                    onChange={toggleModuleStaticHeight}
                    // Disable dynamic height for all input components and case email module
                    // so its content can reach the full height
                    disabled={FULL_HEIGHT_MODULES.includes(selectedModuleInfo.module['type'])}
                    aria-label='Toggle dynamic height'
                    value={selectedModuleInfo.module['module-layout']['dynamic-height']}
                  />
                </ToggleRowWrapper>
                <Spacer height={1} />
                {shouldShowVisibilityToggle && (
                  <ToggleRowWrapper>
                    <ToggleLabelContainer>
                      <Label size='md' weight='bold'>
                        Visibility
                      </Label>
                      <LabelDescription size='sm'>
                        Dynamically control whether the module is hidden.
                      </LabelDescription>
                    </ToggleLabelContainer>
                    <ToggleSlider
                      onChange={() => toggleDynamicVisibilitySetting(!!visible)}
                      value={!!visible}
                    />
                  </ToggleRowWrapper>
                )}
                {visible && (
                  <>
                    <Spacer height={2} />
                    {shouldShowVisibilityAdvancedControls && (
                      <TabBarSwitch>
                        <TabSwitch selected={isConditionalLogic} onClick={setDefaultVisibleRules}>
                          Basic
                        </TabSwitch>
                        <TabSwitch selected={!isConditionalLogic} onClick={setDefaultVisibleCode}>
                          Advanced
                        </TabSwitch>
                      </TabBarSwitch>
                    )}
                    <Spacer height={1} />
                    <ComputedPropertyInput
                      computedProperty={visible}
                      dataSchema={dataSchema}
                      onComputedPropertyUpdate={updateVisibilityComputedProperty}
                      setComputedPropertyValid={(valid: boolean) =>
                        updateInvalidModuleTokens(selectedModuleInfo.module.token, valid)
                      }
                      keyPrefix={selectedModuleInfo.module.token}
                    />
                  </>
                )}
              </ControlPanelContainer>
              <ControlPanelDivider full />
            </>
          )}
          {shouldShowAdvancedOptions && (
            <>
              <ControlPanelContainer>
                <Accordion title='Advanced Options' defaultExpanded={false}>
                  <Spacer height={3} />
                  <ToggleRowWrapper>
                    <ToggleLabelContainer>
                      <Label size='md' weight='bold'>
                        Module Enablement
                      </Label>
                    </ToggleLabelContainer>
                  </ToggleRowWrapper>
                  <Spacer height={1} />
                  <Paragraph>
                    Module will be disabled or enabled depending on return value.
                  </Paragraph>
                  <Spacer height={1} />
                  <ComputedPropertyInput
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    computedProperty={enabled}
                    dataSchema={dataSchema}
                    onComputedPropertyUpdate={updateEnablementComputedProperty}
                  />
                </Accordion>
              </ControlPanelContainer>
              <ControlPanelDivider full />
            </>
          )}
          <ControlPanelContainer
            id={`control-panel__footer--${environmentName}`}
            data-test={`control-panel__footer--${environmentName}`}
          >
            <Button
              id={`control-panel__footer--delete-button--${environmentName}`}
              data-test={`control-panel__footer--delete-button--${environmentName}`}
              onClick={() => {
                deleteModule(selectedModuleInfo.tabToken, selectedModuleToken);
              }}
              variant='basic'
              width='auto'
            >
              Delete
            </Button>
          </ControlPanelContainer>
        </>
      )}
    </Container>
  );
}

export default ControlPanel;
